import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Globe, 
  Share2, 
  Mail, 
  Database, 
  Plug, 
  Settings, 
  ChevronRight, 
  CheckCircle,
  Zap,
  Layers
} from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Integration Category Card
const IntegrationCard = ({ icon: Icon, title, items, color }) => (
  <div 
    className="card p-6 h-full group"
    style={{ 
      background: "var(--background-200)", 
      borderColor: `${color}30`,
      borderTopWidth: "3px",
      borderTopColor: color
    }}
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
      style={{ background: `${color}20` }}
    >
      <Icon size={28} style={{ color: color }} />
    </div>
    <h3 
      className="text-xl font-bold mb-4"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h3>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-200)" }}>
          <CheckCircle size={14} style={{ color: color, flexShrink: 0 }} />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// Feature Row Component
const FeatureRow = ({ icon: Icon, title, description, isReversed }) => (
  <div className={`grid lg:grid-cols-2 gap-12 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
    <div className={isReversed ? 'lg:order-2' : ''}>
      <div 
        className="p-4 rounded-2xl inline-block mb-4"
        style={{ background: "var(--primary-100)" }}
      >
        <Icon size={32} style={{ color: "white" }} />
      </div>
      <h3 
        className="text-2xl md:text-3xl font-bold mb-4"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h3>
      <p 
        className="text-lg"
        style={{ color: "var(--text-200)", lineHeight: "1.7" }}
      >
        {description}
      </p>
    </div>
    <div className={isReversed ? 'lg:order-1' : ''}>
      <div 
        className="rounded-2xl p-8 h-64 flex items-center justify-center"
        style={{ 
          background: "var(--background-300)",
          border: "1px dashed rgba(176, 176, 176, 0.3)"
        }}
      >
        <div className="text-center">
          <Icon size={64} style={{ color: "var(--primary-300)", margin: "0 auto 16px", opacity: 0.5 }} />
          <p style={{ color: "var(--text-300)" }}>{title}</p>
        </div>
      </div>
    </div>
  </div>
);

// Main Integrations Page Component
const IntegrationsPage = () => {
  const marketingIntegrations = [
    {
      icon: Globe,
      title: "Website Integration",
      color: "#0f5fff",
      items: ["Your dealership website", "MachineryMarketed website", "Custom branding"]
    },
    {
      icon: Share2,
      title: "Social Media",
      color: "#0049db",
      items: ["Facebook pages", "Instagram posts", "LinkedIn updates"]
    },
    {
      icon: Mail,
      title: "Email & Newsletters",
      color: "#0038a8",
      items: ["Email campaigns", "Newsletter integration", "Custom databases"]
    },
    {
      icon: Database,
      title: "Equipment Platforms",
      color: "#0f5fff",
      items: ["Industry platforms", "Marketplace listings", "Custom channels"]
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-16">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Plug size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Connect Everything</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                App <span className="text-gradient">Integrations</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed is built to plug into the systems and channels you already use —{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>and the ones you want to use next</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Marketing Integrations Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Marketing & Distribution
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Connect with the platforms that matter to your business
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {marketingIntegrations.map((integration, index) => (
                <IntegrationCard key={index} {...integration} />
              ))}
            </div>
          </div>
        </section>

        {/* Behind the Scenes Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <Layers size={48} style={{ color: "var(--primary-300)", margin: "0 auto 16px" }} />
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-4"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  Behind the Scenes
                </h2>
                <p style={{ color: "var(--text-200)" }}>
                  Designed for flexibility with your existing systems
                </p>
              </div>

              <div className="space-y-20">
                <FeatureRow 
                  icon={Settings}
                  title="DMS Integration"
                  description="Whether you're running a DMS like Equip or Titan, using SharePoint, Outlook, or a service department system — we're switched on enough to integrate. Our technology is designed for flexibility."
                  isReversed={false}
                />
                
                <FeatureRow 
                  icon={Zap}
                  title="Sales Tools & CRMs"
                  description="Using sales tools like Pipedrive, Nutshell, or other CRM and sales software? We can connect. Want appraisal reports to land automatically in a shared folder, inbox, or system? Say the word."
                  isReversed={true}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Custom Integration Highlight */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div 
                className="card p-10 relative overflow-hidden"
                style={{ 
                  background: "linear-gradient(135deg, var(--background-200), var(--background-300))",
                  borderColor: "rgba(15, 95, 255, 0.3)"
                }}
              >
                {/* Background decoration */}
                <div 
                  className="absolute top-0 right-0 w-64 h-64 opacity-10"
                  style={{
                    background: "radial-gradient(circle, var(--primary-300) 0%, transparent 70%)",
                    transform: "translate(30%, -30%)"
                  }}
                />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ background: "var(--primary-100)" }}
                    >
                      <Settings size={32} style={{ color: "white" }} />
                    </div>
                    <h3 
                      className="text-2xl md:text-3xl font-bold"
                      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                    >
                      Custom Integrations
                    </h3>
                  </div>
                  
                  <p 
                    className="text-lg mb-6"
                    style={{ color: "var(--text-200)", lineHeight: "1.7" }}
                  >
                    Listings can be pushed to industry-specific used equipment sales platforms — we won't 
                    lock you into a fixed list. You choose where your machines go, and we customise 
                    distribution to suit your business.
                  </p>
                  
                  <p 
                    className="font-medium"
                    style={{ color: "var(--primary-300)" }}
                  >
                    Our developers make what already works work harder.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Goal Statement */}
        <section className="py-20 relative">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.08) 0%, transparent 70%)"
            }}
          />
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Goal
              </h2>
              <p 
                className="text-xl mb-8"
                style={{ color: "var(--text-200)", lineHeight: "1.8" }}
              >
                Get metal out of your yard faster, while you focus on customers, staff, and keeping 
                all stakeholders satisfied.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto"
            >
              {/* Background */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)"
                }}
              />
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Want to Discuss Custom Integrations?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  Let's talk about how MachineryMarketed can connect with your existing systems.
                </p>
                <Link 
                  to="/#contact" 
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all"
                  style={{ 
                    background: "white", 
                    color: "var(--primary-100)"
                  }}
                >
                  Contact Us <ChevronRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IntegrationsPage;
